import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { MaskedLine, Reveal } from "../components/Reveal";
import EditorialMarquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1559697242-a465f2578a95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrJTIwaG9vZGllJTIwY3VzdG9tJTIwY2xvdGhpbmclMjB0ZWNod2VhciUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODk0MzQ0Mjh8MA&ixlib=rb-4.1.0&q=85";
const CHAPTER_IMGS = {
  craft: "https://images.unsplash.com/photo-1653032352741-47204a1d33e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w5MTYxODd8MHwxfHNlYXJjaHwyfHxzdHJlZXR3ZWFyJTIwZW1icm9pZGVyeSUyMGhvb2RpZSUyMGRldGFpbCUyMGNsb3NlJTIwdXAlMjBmYWJyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc4OTQzNDQzNXww&ixlib=rb-4.1.0&q=85",
  subvert: "https://images.unsplash.com/photo-1565978771542-0db9ab9ad3de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrJTIwaG9vZGllJTIwY3VzdG9tJTIwY2xvdGhpbmclMjB0ZWNod2VhciUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODk0MzQ0Mjh8MA&ixlib=rb-4.1.0&q=85",
  dispatch: "https://images.unsplash.com/photo-1613372281199-ec69ace5e926?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrJTIwaG9vZGllJTIwY3VzdG9tJTIwY2xvdGhpbmclMjB0ZWNod2VhciUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODk0MzQ0Mjh8MA&ixlib=rb-4.1.0&q=85",
};

const CHAPTERS = [
  { n: "01", title: "Craft", img: CHAPTER_IMGS.craft, text: "Every blank is hand-picked: 480gsm fleece, ring-spun cotton, reinforced seams. We print with water-based inks and stitch at 10k density so your design outlives the trend cycle." },
  { n: "02", title: "Subvert", img: CHAPTER_IMGS.subvert, text: "No catalog clones. Upload your art, pick your placement, choose print or embroidery. One-off pieces or full runs — the machine doesn't care, it just executes your vision." },
  { n: "03", title: "Dispatch", img: CHAPTER_IMGS.dispatch, text: "Track your piece through the line: printing, embroidery, quality control, out the door in 48 hours. Real-time status from our floor to your doorstep." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgTextY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <motion.p style={{ y: bgTextY }} className="absolute top-1/4 left-0 font-display text-[22vw] leading-none uppercase text-outline opacity-40 select-none pointer-events-none whitespace-nowrap">
          RAW APPAREL
        </motion.p>
        <div className="max-w-7xl mx-auto px-4 md:px-12 w-full grid lg:grid-cols-12 gap-10 items-center relative z-10">
          <div className="lg:col-span-7 py-24">
            <MaskedLine delay={0.1}><p className="eyebrow mb-6">Custom apparel studio — EST. 2026</p></MaskedLine>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] tracking-tight text-white">
              <MaskedLine delay={0.25}>Wear what</MaskedLine>
              <MaskedLine delay={0.37}>no one else</MaskedLine>
              <MaskedLine delay={0.49}><span className="text-kinetic">can.</span></MaskedLine>
            </h1>
            <MaskedLine delay={0.65}>
              <p className="mt-8 max-w-md text-zinc-400 leading-relaxed">
                Coleman Customs turns your art into heavyweight print and high-density embroidery. No minimums. No compromises. Built to order, dispatched in 48 hours.
              </p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/shop" data-testid="hero-shop-button" className="btn-kinetic">Shop the drop <ArrowRight size={15} /></Link>
              <Link to="/shop?custom=1" data-testid="hero-custom-button" className="btn-ghost">Start a custom <ArrowUpRight size={15} /></Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-14 flex gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500"
            >
              <span>480GSM Fleece</span><span className="text-kinetic">/</span>
              <span>10K Stitch Count</span><span className="text-kinetic">/</span>
              <span>48H Dispatch</span>
            </motion.div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <motion.img
                style={{ y: imgY }}
                src={HERO_IMG}
                alt="Streetwear models in custom Coleman apparel"
                className="clipped-frame w-full aspect-[4/5] object-cover scale-110"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -left-4 bg-kinetic text-white font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3">
                Drop 07 — Now Live
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* FEATURED DROP */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 pt-28">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Latest drop</p>
              <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">Featured pieces</h2>
            </div>
            <Link to="/shop" data-testid="featured-view-all-link" className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-kinetic transition-colors">
              View all <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-grid">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 pt-36" data-testid="manifesto-section">
        <Reveal>
          <p className="eyebrow mb-3">The manifesto</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-16">How we operate</h2>
        </Reveal>
        <div className="space-y-20">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div className={`grid lg:grid-cols-12 gap-8 items-center ${i % 2 ? "" : ""}`}>
                <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="relative">
                    <img src={c.img} alt={c.title} className="clipped-frame w-full aspect-[4/3] object-cover grayscale-[40%] hover:grayscale-0 transition-all duration-500" />
                    <span className="absolute -top-6 -left-2 font-display text-8xl text-outline-orange select-none">{c.n}</span>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1 lg:text-right" : "lg:col-start-7"}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-kinetic mb-4">{c.n} // {c.title}</p>
                  <h3 className="font-display text-4xl md:text-6xl uppercase tracking-tight mb-6">{c.title}</h3>
                  <p className={`text-zinc-400 leading-relaxed max-w-lg ${i % 2 ? "lg:ml-auto" : ""}`}>{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CUSTOM CTA */}
      <section className="mt-36 border-y border-white/10 bg-surface relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-28 relative z-10">
          <Reveal>
            <p className="eyebrow mb-4">Custom studio</p>
            <h2 className="font-display text-4xl md:text-7xl uppercase tracking-tight leading-[0.95]">
              Your art.<br /><span className="text-kinetic">Our machines.</span>
            </h2>
            <p className="mt-8 max-w-md text-zinc-400 leading-relaxed">
              Pick a blank, choose print or embroidery, drop your text or artwork on the front or back. Instant quote, no minimum order.
            </p>
            <Link to="/shop?custom=1" data-testid="cta-custom-button" className="btn-kinetic mt-10">Open the studio <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
        <p className="absolute -bottom-10 right-0 font-display text-[18vw] leading-none uppercase text-outline opacity-30 select-none pointer-events-none">CUSTOM</p>
      </section>
    </div>
  );
}
